import type {RegisterBody, AllowedUrlParams} from "~/api/v1/auth/AuthRepository";
import type { CustomerPortalRequest, CustomerPortalResponse } from "~/api/v2/users/UserRepository";
import {authStore, type User} from "~/stores/auth";

interface RegisterPayload {
    body: RegisterBody;
    params?: AllowedUrlParams;
}

// Interface pour le profil freelance
export interface FreelanceProfile {
    freelance_id: string;
    handle?: string;
    slug?: string;
    first_name: string;
    last_name: string;
    daily_rate?: number;
    location?: string;
    available_to_work: boolean;
    title?: string;
    summary?: string;
    is_visible: boolean;
    picture?: any;
    banner?: any;
    client_cases?: { url: string; title?: string }[];
    twitter?: string;
    linkedin?: string;
    github?: string;
    youtube?: string;
    contact_phone?: string;
    whatsapp?: string;
    contact_email?: string;
    created_at: string;
    updated_at: string;
    experiences: any[];
    skills: string[];
    languages: string[];
    experience?: string;
    mobility?: string;
    distance?: number;
    signed_picture_url: string;
    signed_video_url?: string;
    signed_audio_demo_url?: string;
}


export interface FreelanceProfileResponse {
    profile: FreelanceProfile;
    signed_picture_url?: string;
}

export const useUserStore = defineStore('user', {
    state: () => ({
        user: null as User | null,
        users: [],
        isLoggedIn: false,
        freelanceProfile: {} as FreelanceProfile,
    }),
    actions: {
        async fetchUser(slug: string) {
            const { $api } = useNuxtApp();
            return $api().v2.users.get(slug).then((user: User) => {
                this.user = user;
                return user;
            });
        },

        async fetchUsers(params: Record<string, any> = {}) {
            const { $api } = useNuxtApp();
            return $api().v2.users.list(params).then((response) => {
                this.users = response.data;
                return response;
            });
        },

        // LEGACY
        async uploadAvatar(file: File) {
            const { $api } = useNuxtApp();
            return $api().v1.users.uploadAvatar(file);
        },
        async patchUser(payload: Partial<User>) {
            try {
                const { $api } = useNuxtApp();
                const response = await $api().v1.users.patchSelf(payload);
                
                // Mettre à jour le store auth avec les nouvelles données
                const auth = authStore();
                if (auth.user) {
                    auth.setUser({ ...auth.user, ...payload });
                }
                
                return response;
            } catch (error) {
                console.error('Erreur lors de la mise à jour du profil utilisateur:', error);
                throw error;
            }
        },
        async updateFreelanceProfile(data: Partial<FreelanceProfile>, companyId?: string) {
            try {
                const { $api } = useNuxtApp();

                const response = await $api().v1.users.patchFreelance(data, companyId);

                // Mettre à jour le profil freelance dans le store
                if (this.freelanceProfile) {
                    this.freelanceProfile = {
                        ...this.freelanceProfile,
                        ...data
                    };
                }

                return response;
            } catch (error) {
                console.error('Erreur lors de la mise à jour du profil freelance:', error);
                throw error;
            }
        },
        getProfile: async function () {
            const { $api } = useNuxtApp();
            const auth = authStore();

            const response = await $api().v1.users.getProfile();
            auth.setUser(response);
            return response;
        },
        getFreelanceProfile: async function () {
            try {
                const { $api } = useNuxtApp();
                
                const profile: FreelanceProfileResponse = await $api().v1.users.getProfile();
                this.freelanceProfile = profile;
                this.freelanceProfile.signed_picture_url = profile?.signed_picture_url || '';
                return profile;
            } catch (error) {
                console.error('Erreur lors de la récupération du profil freelance:', error);
                return null;
            }
        },
        
        /**
         * Creates a Stripe Customer Portal session and returns the URL
         * @param returnUrl The URL to redirect the user to after the session
         * @returns The customer portal session URL
         */
        async createCustomerPortalSession(returnUrl: string): Promise<string> {
            try {
                const { $api } = useNuxtApp();
                
                const request: CustomerPortalRequest = {
                    return_url: returnUrl
                };
                
                const response = await $api().v2.users.createCustomerPortalSession(request);
                
                return response.url;
            } catch (error) {
                console.error("Error creating customer portal session:", error);
                throw error;
            }
        },

        logout() {
            this.$reset();
        }
    },
});
